import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Button from "../../../base/button/button";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import classnames from "classnames";
import UpsertPartSupportRequestDocument from "../../../app-specific/part-support/part-support-request-doc";
import PartSupportRequestInformationRow from "../../../app-specific/part-support/part-support-request-info/info-row";
import {PartSupportRequestInformationRowDataGridCellKeys} from "../../../../../core/constants/table/data-grid-column-keys";


const UpsertPartSupportRequestDocumentation = ({
                                                   request,
                                                   terminateProcess,
                                                   warehouseCode,
                                                   canGoBack = false,
                                                   navigateBack,
                                               }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [alreadyDefinedDocuments, setAlreadyDefinedDocuments] = useState([])
    const isMounted = useIsMounted();

    const DataGridSaveConfig = useMemo(() => ({
        name: 'UpsertPartSupportRequestDialogDocumentationDataGrid',
        version: 1,
    }), [])

    /**
     * Fetches the list of template docs from the server for the specific request
     * @type {function(): Promise<void>}
     */
    const getTemplateDocs = useCallback(async () => {
        if (!request?.id)
            return;
        setLoading(true);
        const response = await Api.getPartSupportTemplateFiles(request.id);
        if (!isMounted())
            return;
        setAlreadyDefinedDocuments(response?.uploadedFiles ?? []);
        if (response?.resultFlag) {
            const res = response.data?.allTemplates?.map(e => {
                const definedDocument = response?.data?.uploadedFiles?.find(d => d.partSupportReqTemplateId === e.id)
                return (
                    !!definedDocument
                        ? {
                            ...e,
                            url: definedDocument?.file?.url,
                            fileName: definedDocument?.file?.fileName,
                            selectedTypeId: definedDocument?.typeAndExtension?.id,
                        }
                        : {
                            ...e,
                            url: null,
                            fileName: null,
                            selectedTypeId: null,
                        }
                )
            })
            setDocuments(res ?? []);
        }
        setLoading(false);
    }, [request.id, isMounted])

    /**
     * Loads the template documentations required to complete the process of add request as soon as the component
     * mounts.
     */
    useEffect(() => {
        void getTemplateDocs();
    }, [getTemplateDocs])


    /**
     * Sends an api request for the final submission of the warranty or return request and if the result of the api
     * is successful,
     * @type {function(): Promise<void>}
     */
    const submitWarrantyAndReturnRequest = useCallback(async () => {
        setSubmitting(true);
        const response = await Api.submitPartSupportTemplates(request.id);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            terminateProcess(response.data);
        }
    }, [isMounted, request.id, terminateProcess])

    return (
        <Row className='no-gutters justify-content-center align-items-start w-100'>
            <Col xs={12}>
                <PartSupportRequestInformationRow
                    data={[request]}
                    exclude={[
                        PartSupportRequestInformationRowDataGridCellKeys.vendorCode,
                        PartSupportRequestInformationRowDataGridCellKeys.vendorName,
                    ]}
                    saveConfig={DataGridSaveConfig}
                />
            </Col>
            {
                loading
                    ? (
                        <Row className={'w-100 p-3 no-gutters loading request-doc'}>
                            <Col xs={12} sm={6} lg={4} xl={3} className='p-2'>
                                <div/>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3} className='p-2'>
                                <div/>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3} className='p-2'>
                                <div/>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3} className='p-2'>
                                <div/>
                            </Col>
                        </Row>
                    ) :
                    (
                        <Row className={'w-100 px-3 pb-3 pt-5 no-gutters'}>
                            {documents?.length > 0 ?
                                documents?.map(doc => (
                                    <UpsertPartSupportRequestDocument
                                        key={doc.id}
                                        doc={doc}
                                        setDocuments={setDocuments}
                                        setAlreadyDefinedDocuments={setAlreadyDefinedDocuments}
                                        alreadyDefinedDocuments={alreadyDefinedDocuments}
                                        userId={request?.user?.id}
                                        customerId={request?.customer?.id}
                                        warehouseCode={warehouseCode}
                                        requestId={request?.id}
                                    />
                                ))
                                : (
                                    <p className={'w-100 my-4 mx-3 text-sm text-center'}>
                                        There is no need to upload any documents for this particular part. You can just
                                        submit this request.
                                    </p>
                                )
                            }
                        </Row>
                    )
            }
            <div className={'w-100 my-2'}/>
            <Row className={classnames('no-gutters align-items-start w-100 footer',
                {
                    "justify-content-between ": canGoBack,
                    "justify-content-end": !canGoBack
                })}>
                {
                    canGoBack &&
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => navigateBack && navigateBack()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {submitting ? "Submitting..." : "Back"}
                    </Button>
                }
                <Button
                    disabled={documents?.filter(e => !e?.url && e.isRequired).length > 0 || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={submitWarrantyAndReturnRequest}
                    borderRadius={'8px 0 8px 0'}
                >
                    {submitting ? "Submitting..." : "Next"}
                </Button>
            </Row>
        </Row>
    );
}


export default UpsertPartSupportRequestDocumentation;
